import {FormControl, FormField} from 'platform/components';
import {Grid} from 'platform/foundation';
import {useCurrencySymbolFormatter} from 'platform/locale';

import {useState} from 'react';
import {UseFormReturn, useWatch} from 'react-hook-form';

import {indexBy, isNil} from 'ramda';
import {isString} from 'ramda-adjunct';

import {RentalOrderResponseBody} from '@dms/api/rental';
import i18n from '@dms/i18n';
import {useTenant, useVatRatesOptions} from '@dms/shared';

import {Nullish, RequiredTestIdProps, suffixTestId, useDebouncedCallback} from 'shared/web';

import {roundToTwoDecimals} from '../../RentalOrderHeader/utils/roundToTwoDecimals';
import {useGetHandleUpdateChargedKilometers} from '../hooks/useGetHandleUpdateChargedKilometers';
import {ReturnVehicleFormValues} from '../types/ReturnVehicleFormValues';

type PriceFieldPaths =
  | 'rentPrice.rentalPrice'
  | 'rentPrice.pricePerKilometer'
  | 'rentPrice.deliveryPrice'
  | 'rentPrice.coPayment'
  | 'rentPrice.contractPenalty'
  | 'rentPrice.otherPrice';

interface StandardPriceRowProps extends RequiredTestIdProps {
  control: FormControl<ReturnVehicleFormValues>;
  formApi: UseFormReturn<ReturnVehicleFormValues>;
  groupName: PriceFieldPaths;
  withVatLabel: string;
  withoutVatLabel: string;
  areFieldsRequired?: boolean;
  rentalOrder: RentalOrderResponseBody | Nullish;
}

export function StandardPriceRow(props: StandardPriceRowProps) {
  const isTotalPriceGroup = props.groupName === 'rentPrice.rentalPrice';
  const [vatType, isCustomPriceEnabled] = useWatch({
    control: props.control,
    name: [`${props.groupName}.vatType`, 'isCustomPriceEnabled'],
  });

  const [lastTouchedField, setLastTouchedField] = useState<'withVat' | 'withoutVat'>('withoutVat');

  const [updateChargedKilometers] = useGetHandleUpdateChargedKilometers({
    formApi: props.formApi,
    rentalOrder: props.rentalOrder,
  });

  const [vatRatesOptions] = useVatRatesOptions();
  const vatRatesByCode = indexBy((vatRate) => vatRate.value, vatRatesOptions);
  const formatCurrencySymbol = useCurrencySymbolFormatter();
  const {tenantCurrency} = useTenant();

  const handleRecalculatePriceWithVat = useDebouncedCallback(
    (priceWithoutVat: number | null, changedVatRate?: string) => {
      setLastTouchedField('withoutVat');
      const absoluteVatRate = vatRatesByCode?.[changedVatRate ?? vatType ?? '']?.rate;

      if (isNil(priceWithoutVat) || isNil(absoluteVatRate)) {
        return;
      }

      const rate = parseFloat(absoluteVatRate);
      const newPriceWithVat = roundToTwoDecimals(priceWithoutVat * (1 + rate / 100));
      const newVatPrice = roundToTwoDecimals(newPriceWithVat - priceWithoutVat);
      props.formApi.setValue(`${props.groupName}.withVat`, newPriceWithVat);
      props.formApi.setValue(`${props.groupName}.vat`, newVatPrice);

      if (props.groupName === 'rentPrice.pricePerKilometer') {
        const [milage, totalFreeKilometers] = props.formApi.getValues([
          'actualMileage',
          'rentPrice.totalFreeKilometers',
        ]);
        updateChargedKilometers(milage, totalFreeKilometers);
      }
    },
    200
  );

  const handleRecalculatePriceWithoutVat = useDebouncedCallback(
    (priceWithVat: number | null, changedVatRate?: string) => {
      setLastTouchedField('withVat');
      const absoluteVatRate = vatRatesByCode?.[changedVatRate ?? vatType ?? '']?.rate;

      if (isNil(priceWithVat) || isNil(absoluteVatRate)) {
        return;
      }

      const rate = parseFloat(absoluteVatRate);
      const newPriceWithoutVat = roundToTwoDecimals(priceWithVat / (1 + rate / 100));
      const newVatPrice = roundToTwoDecimals(priceWithVat - newPriceWithoutVat);
      props.formApi.setValue(`${props.groupName}.withoutVat`, newPriceWithoutVat);
      props.formApi.setValue(`${props.groupName}.vat`, newVatPrice);

      if (props.groupName === 'rentPrice.pricePerKilometer') {
        const [milage, totalFreeKilometers] = props.formApi.getValues([
          'actualMileage',
          'rentPrice.totalFreeKilometers',
        ]);
        updateChargedKilometers(milage, totalFreeKilometers);
      }
    },
    200
  );

  const handleRecalculateAfterVatRateChange = (vatRate: string | number | null) => {
    if (!isString(vatRate)) {
      return;
    }

    const values = props.formApi.getValues(props.groupName);

    if (lastTouchedField === 'withoutVat' && values.withoutVat) {
      handleRecalculatePriceWithVat(values.withoutVat, vatRate);
    }

    if (lastTouchedField === 'withVat' && values.withVat) {
      handleRecalculatePriceWithoutVat(values.withVat, vatRate);
    }
  };

  return (
    <Grid columns={4}>
      <FormField
        control={props.control}
        type="currency"
        isRequired={props.areFieldsRequired && !isCustomPriceEnabled}
        name={`${props.groupName}.withoutVat`}
        label={props.withoutVatLabel}
        currency={tenantCurrency}
        decimalPlaces={2}
        isDisabled={isCustomPriceEnabled || isTotalPriceGroup}
        onChange={(value) => handleRecalculatePriceWithVat(value)}
        data-testid={suffixTestId(`priceWithoutVat`, props)}
      />

      <FormField
        control={props.control}
        type="currency"
        isRequired={props.areFieldsRequired && !isCustomPriceEnabled}
        name={`${props.groupName}.withVat`}
        label={props.withVatLabel}
        currency={tenantCurrency}
        decimalPlaces={2}
        isDisabled={isCustomPriceEnabled || isTotalPriceGroup}
        onChange={(value) => handleRecalculatePriceWithoutVat(value)}
        data-testid={suffixTestId(`priceWithVat`, props)}
      />

      <FormField
        control={props.control}
        type="currency"
        name={`${props.groupName}.vat`}
        decimalPlaces={2}
        label={`${i18n.t('entity.vehicle.labels.vat')} ${formatCurrencySymbol(tenantCurrency)}`}
        currency={tenantCurrency}
        isDisabled
        data-testid={suffixTestId(`priceVat`, props)}
      />

      <FormField
        control={props.control}
        type="choice"
        name={`${props.groupName}.vatType`}
        label={i18n.t('entity.vehicle.labels.vat')}
        options={vatRatesOptions}
        isNotClearable
        isDisabled={isCustomPriceEnabled}
        onChange={(value) => handleRecalculateAfterVatRateChange(value)}
        data-testid={suffixTestId(`priceVatRate`, props)}
      />
    </Grid>
  );
}
